<template>
  <div class="root_container" :style="styleObject">
    <div
      class="cart-dock"
      :class="{ 'cart-dock--empty': itemQuantity <= 0 }"
      v-if="currentRouteName && ['Order', 'Queue', 'Category', ].includes(currentRouteName)"
    >
      <!-- MTH065: the voucher strip that sat above the pill is gone; voucher
           messaging now lives only on MyCart / Checkout. -->
      <!-- compact cart pill — FloatingCartPill keeps .root_order_total so the fly-to-cart target stays valid -->
      <FloatingCartPill
        :item-count="itemQuantity"
        :total="Number(calculateTotal())"
        :pulse-token="pulseToken"
        @open="gotoCheckout"
      />
    </div>
    <BottomNav
      :active="activeNavTab"
      :hide-member="!!(branch && branch.hide_member_login_moo == 1)"
      @navigate="onNavTab"
    />
  </div>
</template>

<script>
import EventBus from "vue3-eventbus";
import { useRoute } from 'vue-router';
import { localStorageName } from '@/config/utils';
import BottomNav from "@/components/BottomNav.vue";
import FloatingCartPill from "@/components/FloatingCartPill.vue";
import { cartSubtotal, cartTotalPayable } from "@/config/cartTotal";
import { promoDiscountAmount } from "@/config/promoCode";
import { DINE_IN } from "@/config/const";

export default {
  name: "OrderFooter",
  components: { BottomNav, FloatingCartPill },
  setup() {
    const route = useRoute();
    const currentRouteName = route.name;

    return {
      currentRouteName
    };
  },
  data() {
    return {
      merchantCode: "",
      branchCode: "",
      pathType: "",
      tableCode: "",
      branch: JSON.parse(localStorage.getItem(localStorageName('BRANCH_INFO'), '')),
      itemQuantity: 0,
      itemSelect: [],
      memberInfo: JSON.parse(localStorage.getItem(localStorageName('memberInfo'), '')),
      totalVoucherDiscount: 0,
      isLogined: false,
      isYourFriendLogined: false,
      cartName: 'ORDER_INFO',
      queue: false,
      promoCodeApplied: null,
      isPromoCodeApplied: false,
      diningOption: localStorage.getItem(localStorageName("DINING_OPTION")) ?? DINE_IN,
      pulseToken: 0
    };
  },
  async created() {
    this.cartName = await this.$store.dispatch('cartName');
    this.merchantCode = this.$store.state.merchantCode;
    this.branchCode = this.$store.state.branchCode;
    this.pathType = this.$route.params.type;
    this.tableCode = this.$route.params.table_code;
    this.tableInfo = JSON.parse(localStorage.getItem('table_' + (this.merchantCode || '').toLowerCase() + '_' + (this.branchCode || '').toLowerCase() + '_' + (this.tableCode || '').toLowerCase(), {}));
    this.queue = this.$route.query.queue ?? false;

    if (this.currentRouteName == 'Queue') {
      this.queue = true;
      this.cartName = this.cartName.replace('ORDER_INFO_', 'ORDER_QUEUE_INFO_');
    }

    if (this.tableInfo) {
      this.isYourFriendLogined = this.queue ? false : this.tableInfo.is_member_order ?? false;
    }

    const savedPromoCode = localStorage.getItem(localStorageName("PROMO_CODE_APPLIED"));
    if (savedPromoCode) {
      this.promoCodeApplied = JSON.parse(savedPromoCode);
      this.isPromoCodeApplied = true;
      this.totalVoucherDiscount = this.promoCodeApplied.discount_amount || 0;
    }

    let orderInfo = JSON.parse(localStorage.getItem(this.cartName, null)) ?? [];
    this.itemSelect = orderInfo;

    orderInfo.forEach((item) => {
      this.itemQuantity += item.quantity;
    });

    if(this.memberInfo || this.isYourFriendLogined) {
      this.isLogined = true;
    }
  },
  mounted() {
    EventBus.on("DiningOptionChanged", () => {
      this.diningOption = localStorage.getItem(localStorageName("DINING_OPTION")) ?? DINE_IN;
    });
    EventBus.on("AddToCartSuccess", async () => {
      setTimeout(() => {
        this.itemQuantity = 0;
        let orderInfo =
          JSON.parse(localStorage.getItem(this.cartName, null)) ?? [];
        this.itemSelect = orderInfo;
        orderInfo.forEach((item) => {
          this.itemQuantity += item.quantity;
        });
        // MTH022: pulse the cart pill to acknowledge the add
        this.pulseToken++;
      }, 300);
    });
    EventBus.on("ChangeItemQuantitySuccess", async () => {
      setTimeout(() => {
        this.itemQuantity = 0;
        let orderInfo =
          JSON.parse(localStorage.getItem(this.cartName, null)) ?? [];
        this.itemSelect = orderInfo;
        orderInfo.forEach((item) => {
          this.itemQuantity += item.quantity;
        });
      }, 300);
    });
  },
  computed: {
    styleObject() {
      return {
        "--color": this.$store.state.mainThemeColorTheme2
          ? this.$store.state.mainThemeColorTheme2
          : this.$store.state.color,
      };
    },
    // Map the current route to the BottomNav tab key.
    activeNavTab() {
      if (this.currentRouteName === "MyReward") return "rewards";
      if (this.currentRouteName === "OrderHistory") return "orders";
      if (this.currentRouteName === "Profile") return "profile";
      return "menu";
    },
  },
  methods: {
    // BottomNav taps route through the existing goto* logic so navigation
    // behaviour (queue handling, cart save, etc.) is preserved.
    onNavTab(key) {
      if (key === "rewards") return this.gotoMyReward();
      if (key === "orders") return this.gotoOrderHistory();
      if (key === "profile") return this.gotoProfile();
      return this.gotoHome();
    },
    gotoCheckout() {
      // Pill/checkout goes straight to MyCart; the upsell "Popular items you
      // might like to add" screen is intentionally skipped in this flow.
      let query = {};
      if (this.queue) {
        query = { queue: true };
      }
      this.gotoPath("MyCart", query);
    },

    gotoAddress() {
      if (this.branch.destination_link) {
        window.open(this.branch.destination_link, "_blank");
      }
    },

    // Pill shows the Total Payable MyCart will show (service charge, GST, promo,
    // BCRS), not just the items. A member voucher is priced by the API on MyCart.
    calculateTotal() {
      const subtotal = cartSubtotal(this.itemSelect, this.isLogined);
      const promoApplied = !!(this.isPromoCodeApplied && this.promoCodeApplied);
      // MTH102: promo rounded half-up to the cent, as MyCart does.
      const promoDiscount = promoApplied
        ? Math.round((promoDiscountAmount(this.promoCodeApplied, subtotal, this.itemSelect, this.isLogined) + Number.EPSILON) * 100) / 100
        : 0;

      return cartTotalPayable({
        items: this.itemSelect,
        branch: this.branch,
        diningOption: this.diningOption,
        useMemberPrice: this.isLogined,
        promoApplied,
        promoDiscount,
        serviceChargeOnDiscounted: parseInt(this.branch?.service_charge_on_discounted_subtotal) === 1,
      }).toFixed(2);
    },


    gotoProfile() {
      let query = {};

      if (this.queue) {
        query = {
            queue: true
        }
      }

      this.gotoPath("Profile", query);
    },

    gotoHome() {
      this.gotoPath(this.queue ? "Queue" : "Order");
    },

    gotoOrderHistory() {
      if (!this.queue) {
        this.gotoPath("OrderHistory");
      }
    },

    gotoMyReward() {
      if (this.currentRouteName != 'Queue') {
        if (!this.queue) {
          this.gotoPath("MyReward");
        }
      }
    },

    gotoPath(name, query = {}) {
      this.$router.push({ name: name, params: {
        merchant_code: this.merchantCode,
        type: this.pathType,
        branch_code: this.branchCode,
        table_code: this.tableCode
      }, query: query });
    },
  },
};
</script>

<style scoped>
/* floating cart dock: fixed, compact centered pill floating above the bottom nav
   (MTH022: fixed so it overlays content and the category sidebar can run full-height
   down to the bottom nav instead of being cut at the pill's top edge) */
.cart-dock {
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(84px + env(safe-area-inset-bottom, 0px));
  z-index: 41;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0 10px 6px;
  pointer-events: none;
}
.cart-dock > * {
  pointer-events: auto;
}
/* MTH093: hide the pill while the cart is empty. Kept in the DOM (visibility
   rather than v-if) so Order.vue's fly-to-cart still finds .root_order_total
   and the first added item flies to the spot the pill is about to appear in. */
.cart-dock--empty {
  visibility: hidden;
  opacity: 0;
}
.cart-dock--empty > * {
  pointer-events: none;
}
.div_quantity {
  padding: 5px;
  position: relative;
  width: 15%;
}
.text_quantity {
  position: absolute;
  top: 0px;
  width: 20px;
  height: 20px;
  background: orangered;
  border-radius: 20px;
  align-items: center;
  justify-content: center;
  display: flex;
  color: white;
  font-size: 10px;
}
.text_total_order {
  font-size: 20px;
  font-weight: 800;
}
.text_total_voucher_discount {
  align-items: center;
  justify-content: center;
  display: flex;
  height: 30px;
  align-self: center;
  color: black;
  font-size: 12px;
  font-weight: 600;
  text-decoration: line-through;
}
.btn_proceed {
  background-color: orangered;
  border-radius: 5px;
  width: 25%;
  align-items: center;
  justify-content: center;
  display: flex;
  height: 30px;
  align-self: center;
  color: white;
  font-weight: bold;
  height: 55px;
}
.root_my_reward {
  display: flex;
  width: 100%;
  background: white;
  height: 100%;
  justify-content: space-between;
  height: 52px;
}
.root_my_reward > div {
  padding-top: 5px;
  border-top: 0.5px solid lightgrey;
}
.root_my_reward > div.active {
  color: var(--color) !important;
  border-color: var(--color);
}

.root_my_reward > div.active .icon_footer {
  color: var(--color) !important;
}
header {
  background: var(--color);
  z-index: 99999;
  width: 100%;
  height: 100%;
}

header > div {
  padding: 15px 24px 10px 24px;
}

header .material-icons-round {
  font-size: 32px;
}

header .outlet-name {
  font-size: 16px;
  font-weight: bold;
}

header .outlet-address {
  font-size: 11px;
  margin-top: -3px;
  cursor: pointer;
}

header .cart-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background-color: #ffffff;
  border-radius: 50%;
  cursor: pointer;
}

header .cart-btn .quantity {
  position: absolute;
  top: -12px;
  right: -12px;
  padding: 2px 7px;
  background-color: #ffffff;
  border-radius: 50%;
  font-size: 13px;
  color: var(--color);
  font-weight: 500;
}

@keyframes itemQuantityFromTop {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(0);
  }
}

header .cart-btn .quantity > div {
  animation: 0.2s ease-out 0s 1 itemQuantityFromTop;
}

header .cart-btn .material-icons-round {
  font-size: 20px;
  color: var(--color);
}
.icon_footer {
  color: gray;
  width: 100%;
}
</style>
